import { Box, CircularProgress, Flex, Image } from "@chakra-ui/react";
import {
  SyntheticEvent,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useObjectUrl } from "src/hooks/useObjectUrl";
import { DocViewerContext } from "../state";
import { resetState, setNumPages, setZoomLevel } from "../state/actions";
import { calculateOptimalZoom } from "../state/utils";
import { DocRenderer } from "./DocRenderer";

export const ImageRenderer: DocRenderer = ({ documentData, contentType }) => {
  const imageData = useMemo(
    () => documentData && new Blob([documentData], { type: contentType }),
    [documentData, contentType]
  );
  return <BaseImageRenderer imageData={imageData} />;
};

export const BaseImageRenderer = ({ imageData }: { imageData?: Blob }) => {
  // Covers async decoding, e.g. HEIC conversion after the blob has downloaded.
  if (!imageData) {
    return (
      <Flex
        width="100%"
        height="100%"
        alignItems="center"
        justifyContent="center"
      >
        <CircularProgress isIndeterminate color="primary.500" />
      </Flex>
    );
  }

  return <ImageContent imageData={imageData} />;
};

const ImageContent = ({ imageData }: { imageData: Blob }) => {
  const { state, dispatch } = useContext(DocViewerContext);
  const containerRef = useRef<HTMLDivElement>(null);

  const url = useObjectUrl(imageData) ?? undefined;

  const [dimension, setDimension] = useState<{
    width: number;
    height: number;
  }>({ width: 0, height: 0 });

  const [containerWidth, setContainerWidth] = useState<number>(0);
  const [containerHeight, setContainerHeight] = useState<number>(0);

  // Update container dimensions on resize
  useEffect(() => {
    const updateContainerDimensions = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
        setContainerHeight(containerRef.current.offsetHeight);
      }
    };

    // Initial measurement
    updateContainerDimensions();

    // Set up resize observer
    const resizeObserver = new ResizeObserver(updateContainerDimensions);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // Update zoom level when container dimensions or image dimensions change
  useEffect(() => {
    if (
      dimension.width > 0 &&
      dimension.height > 0 &&
      containerWidth > 0 &&
      containerHeight > 0
    ) {
      const optimalZoom = calculateOptimalZoom(
        dimension.width,
        dimension.height,
        containerWidth,
        containerHeight
      );
      dispatch(setZoomLevel(optimalZoom));
    }
  }, [
    dimension.width,
    dimension.height,
    containerWidth,
    containerHeight,
    dispatch,
  ]);

  const transformation = useMemo<string>(
    () => `rotate(${state.rotation}deg) scale(${state.zoomLevel})`,
    [state]
  );

  const onImageLoad = ({ target }: SyntheticEvent<HTMLImageElement, Event>) => {
    dispatch(resetState());
    dispatch(setNumPages(1));

    const { naturalWidth, naturalHeight } = target as HTMLImageElement;
    setDimension({ width: naturalWidth, height: naturalHeight });
  };

  const { boundingBoxLength, marginTop } = useMemo(() => {
    const imageWidth = dimension.width * state.zoomLevel;
    const imageHeight = dimension.height * state.zoomLevel;
    const boundingBoxLength = Math.max(imageWidth, imageHeight);
    const marginTop =
      containerHeight < boundingBoxLength
        ? 0
        : containerHeight / 2 - boundingBoxLength / 2;
    return {
      boundingBoxLength: boundingBoxLength + "px",
      marginTop: marginTop + "px",
    };
  }, [containerHeight, dimension.height, dimension.width, state.zoomLevel]);

  return (
    <Box ref={containerRef} width="100%" height="100%" overflow="auto">
      {/*
        When an image is transformed with CSS (rotate/scale), the transformation only affects
        visual rendering but not the element's layout dimensions. This creates scrolling issues
        because the parent container doesn't account for the transformed bounds.

        By setting this container's dimensions to the maximum of width/height, we ensure the
        scrollable parent has enough space to accommodate the image in any rotation (0°, 90°, 180°, or 270°).
        */}
      <Box
        width={boundingBoxLength}
        height={boundingBoxLength}
        display="flex"
        alignItems="center"
        justifyContent="center"
        /**
         Old school way of centering element since we can't use flex justify-content and align-items
         since it will mess up with viewport scrolling.
         */
        marginX="auto"
        marginTop={marginTop}
      >
        <Image
          src={url}
          onLoad={onImageLoad}
          transform={transformation}
          transformOrigin="center"
        />
      </Box>
    </Box>
  );
};
